import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiAlertCircle,
    FiCheckCircle,
    FiLoader,
    FiX,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    allocateExistingPayment,
    allocatingPaymentSelector,
    clearPaymentAllocationError,
    clearPaymentAllocationSuccess,
    fetchCustomerOutstandingInvoices,
    loadingOutstandingSelector,
    outstandingDocumentsSelector,
    paymentAllocationErrorSelector,
    paymentAllocationSuccessSelector,
} from "../../../../../../../store/billing/paymentAllocationSlice";

import "./AllocatePaymentDialog.css";

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const parsedValue =
        Number(value);

    return Number.isFinite(
        parsedValue
    )
        ? parsedValue
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }
    ).format(date);
};

const getPaymentId = (
    payment
) =>
    payment?._id ||
    payment?.paymentId ||
    "";

const getCustomerId = (
    payment
) =>
    payment?.customer?._id ||
    payment?.customer ||
    payment?.customerId ||
    "";

const getPaymentAmount = (
    payment
) =>
    roundMoney(
        payment?.amount ||
        0
    );

const getAvailableAmount = (
    payment
) => {
    if (
        payment?.unallocatedAmount !==
            undefined &&
        payment?.unallocatedAmount !==
            null
    ) {
        return roundMoney(
            payment.unallocatedAmount
        );
    }

    return Math.max(
        roundMoney(
            getPaymentAmount(
                payment
            ) -
                toNumber(
                    payment?.allocatedAmount
                )
        ),
        0
    );
};

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const AllocatePaymentDialog =
    ({
        open,
        payment,
        onClose,
        onAllocated,
    }) => {
        const dispatch =
            useDispatch();

        const outstandingDocuments =
            useSelector(
                outstandingDocumentsSelector
            ) || [];

        const loadingOutstanding =
            useSelector(
                loadingOutstandingSelector
            );

        const allocating =
            useSelector(
                allocatingPaymentSelector
            );

        const error =
            useSelector(
                paymentAllocationErrorSelector
            ) || "";

        const successMessage =
            useSelector(
                paymentAllocationSuccessSelector
            ) || "";

        const [
            allocationValues,
            setAllocationValues,
        ] = useState({});

        const paymentId =
            getPaymentId(
                payment
            );

        const customerId =
            getCustomerId(
                payment
            );

        const availableAmount =
            getAvailableAmount(
                payment
            );

        /*
        |--------------------------------------------------------------------------
        | LOAD OUTSTANDING INVOICES
        |--------------------------------------------------------------------------
        */

        useEffect(() => {
            if (
                !open ||
                !customerId
            ) {
                return;
            }

            dispatch(
                clearPaymentAllocationError()
            );

            dispatch(
                clearPaymentAllocationSuccess()
            );

            dispatch(
                fetchCustomerOutstandingInvoices({
                    customerId,
                    page: 1,
                    limit: 100,
                })
            );
        }, [
            customerId,
            dispatch,
            open,
        ]);

        /*
        |--------------------------------------------------------------------------
        | RESET FORM
        |--------------------------------------------------------------------------
        */

        useEffect(() => {
            if (!open) {
                setAllocationValues(
                    {}
                );
            }
        }, [open]);

        /*
        |--------------------------------------------------------------------------
        | TOTALS
        |--------------------------------------------------------------------------
        */

        const selectedAllocations =
            useMemo(
                () =>
                    outstandingDocuments
                        .map(
                            (
                                document
                            ) => {
                                const amount =
                                    roundMoney(
                                        allocationValues[
                                            document._id
                                        ] ||
                                            0
                                    );

                                if (
                                    amount <=
                                    0
                                ) {
                                    return null;
                                }

                                return {
                                    documentId:
                                        document._id,

                                    amount,

                                    notes:
                                        `Allocated against ${document.documentNumber}`,
                                };
                            }
                        )
                        .filter(
                            Boolean
                        ),
                [
                    allocationValues,
                    outstandingDocuments,
                ]
            );

        const totalAllocation =
            useMemo(
                () =>
                    roundMoney(
                        selectedAllocations.reduce(
                            (
                                total,
                                allocation
                            ) =>
                                total +
                                toNumber(
                                    allocation.amount
                                ),
                            0
                        )
                    ),
                [
                    selectedAllocations,
                ]
            );

        const remainingAmount =
            Math.max(
                roundMoney(
                    availableAmount -
                        totalAllocation
                ),
                0
            );

        const isOverAllocated =
            totalAllocation >
            availableAmount;

        /*
        |--------------------------------------------------------------------------
        | INPUT CHANGE
        |--------------------------------------------------------------------------
        */

        const handleAmountChange =
            (
                document,
                value
            ) => {
                const rawValue =
                    String(
                        value ||
                            ""
                    );

                if (
                    rawValue ===
                    ""
                ) {
                    setAllocationValues(
                        (
                            current
                        ) => ({
                            ...current,
                            [document._id]:
                                "",
                        })
                    );

                    return;
                }

                const numericValue =
                    Math.max(
                        toNumber(
                            rawValue
                        ),
                        0
                    );

                const maximumAllowed =
                    Math.min(
                        toNumber(
                            document
                                ?.balanceAmount
                        ),
                        availableAmount
                    );

                setAllocationValues(
                    (
                        current
                    ) => ({
                        ...current,

                        [document._id]:
                            Math.min(
                                numericValue,
                                maximumAllowed
                            ),
                    })
                );
            };

        /*
        |--------------------------------------------------------------------------
        | AUTO ALLOCATE
        |--------------------------------------------------------------------------
        */

        const handleAutoAllocate =
            () => {
                let remaining =
                    availableAmount;

                const nextValues =
                    {};

                outstandingDocuments.forEach(
                    (
                        document
                    ) => {
                        if (
                            remaining <=
                            0
                        ) {
                            nextValues[
                                document._id
                            ] = "";

                            return;
                        }

                        const balance =
                            toNumber(
                                document
                                    ?.balanceAmount
                            );

                        const allocation =
                            Math.min(
                                balance,
                                remaining
                            );

                        nextValues[
                            document._id
                        ] =
                            allocation >
                            0
                                ? allocation
                                : "";

                        remaining =
                            roundMoney(
                                remaining -
                                    allocation
                            );
                    }
                );

                setAllocationValues(
                    nextValues
                );
            };

        /*
        |--------------------------------------------------------------------------
        | CLEAR
        |--------------------------------------------------------------------------
        */

        const handleClear =
            () => {
                setAllocationValues(
                    {}
                );

                dispatch(
                    clearPaymentAllocationError()
                );

                dispatch(
                    clearPaymentAllocationSuccess()
                );
            };

        /*
        |--------------------------------------------------------------------------
        | SUBMIT
        |--------------------------------------------------------------------------
        */

        const handleSubmit =
            async () => {
                dispatch(
                    clearPaymentAllocationError()
                );

                dispatch(
                    clearPaymentAllocationSuccess()
                );

                if (
                    !paymentId
                ) {
                    return;
                }

                if (
                    selectedAllocations.length ===
                    0
                ) {
                    return;
                }

                if (
                    isOverAllocated
                ) {
                    return;
                }

                const action =
                    await dispatch(
                        allocateExistingPayment({
                            paymentId,
                            allocations:
                                selectedAllocations,
                        })
                    );

                if (
                    allocateExistingPayment.fulfilled.match(
                        action
                    )
                ) {
                    if (
                        typeof onAllocated ===
                        "function"
                    ) {
                        onAllocated(
                            action.payload
                        );
                    }

                    setTimeout(
                        () => {
                            handleClose();
                        },
                        500
                    );
                }
            };

        /*
        |--------------------------------------------------------------------------
        | CLOSE
        |--------------------------------------------------------------------------
        */

        const handleClose =
            () => {
                if (
                    allocating
                ) {
                    return;
                }

                setAllocationValues(
                    {}
                );

                dispatch(
                    clearPaymentAllocationError()
                );

                dispatch(
                    clearPaymentAllocationSuccess()
                );

                if (
                    typeof onClose ===
                    "function"
                ) {
                    onClose();
                }
            };

        if (!open) {
            return null;
        }

        return (
            <div
                className="allocate-payment-dialog-overlay"
                role="presentation"
                onMouseDown={(
                    event
                ) => {
                    if (
                        event.target ===
                        event.currentTarget
                    ) {
                        handleClose();
                    }
                }}
            >
                <div
                    className="allocate-payment-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="allocate-payment-title"
                >
                    <header className="allocate-payment-dialog-header">
                        <div>
                            <h3 id="allocate-payment-title">
                                Allocate Payment
                            </h3>

                            <p>
                                Apply the available payment amount to outstanding invoices.
                            </p>
                        </div>

                        <button
                            type="button"
                            className="allocate-payment-close"
                            disabled={
                                allocating
                            }
                            onClick={
                                handleClose
                            }
                        >
                            <FiX />
                        </button>
                    </header>

                    <section className="allocate-payment-info">
                        <div>
                            <span>
                                Receipt Number
                            </span>

                            <strong>
                                {payment
                                    ?.receiptNumber ||
                                    "-"}
                            </strong>
                        </div>

                        <div>
                            <span>
                                Payment Amount
                            </span>

                            <strong>
                                {formatCurrency(
                                    getPaymentAmount(
                                        payment
                                    )
                                )}
                            </strong>
                        </div>

                        <div>
                            <span>
                                Available Amount
                            </span>

                            <strong className="allocate-payment-available">
                                {formatCurrency(
                                    availableAmount
                                )}
                            </strong>
                        </div>
                    </section>

                    {!customerId && (
                        <div className="allocate-payment-message allocate-payment-message-error">
                            <FiAlertCircle />

                            Customer ID is missing from this payment.
                        </div>
                    )}

                    {error && (
                        <div className="allocate-payment-message allocate-payment-message-error">
                            <FiAlertCircle />

                            {error}
                        </div>
                    )}

                    {successMessage && (
                        <div className="allocate-payment-message allocate-payment-message-success">
                            <FiCheckCircle />

                            {successMessage}
                        </div>
                    )}

                    <section className="allocate-payment-toolbar">
                        <div>
                            <strong>
                                Outstanding Invoices
                            </strong>

                            <span>
                                Enter the amount to allocate against each invoice.
                            </span>
                        </div>

                        <div className="allocate-payment-toolbar-actions">
                            <button
                                type="button"
                                disabled={
                                    loadingOutstanding ||
                                    allocating ||
                                    availableAmount <=
                                        0
                                }
                                onClick={
                                    handleClear
                                }
                            >
                                Clear
                            </button>

                            <button
                                type="button"
                                disabled={
                                    loadingOutstanding ||
                                    allocating ||
                                    availableAmount <=
                                        0 ||
                                    outstandingDocuments.length ===
                                        0
                                }
                                onClick={
                                    handleAutoAllocate
                                }
                            >
                                Auto Allocate
                            </button>
                        </div>
                    </section>

                    <div className="allocate-payment-table-wrap">
                        <table className="allocate-payment-table">
                            <thead>
                                <tr>
                                    <th>
                                        Invoice
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th className="allocate-payment-text-right">
                                        Total
                                    </th>

                                    <th className="allocate-payment-text-right">
                                        Paid
                                    </th>

                                    <th className="allocate-payment-text-right">
                                        Balance
                                    </th>

                                    <th className="allocate-payment-allocation-column">
                                        Allocate
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {loadingOutstanding ? (
                                    <tr>
                                        <td
                                            colSpan="6"
                                            className="allocate-payment-loading"
                                        >
                                            <FiLoader className="allocate-payment-spin" />

                                            Loading outstanding invoices...
                                        </td>
                                    </tr>
                                ) : outstandingDocuments.length ===
                                  0 ? (
                                    <tr>
                                        <td
                                            colSpan="6"
                                            className="allocate-payment-empty"
                                        >
                                            No outstanding invoices found for this customer.
                                        </td>
                                    </tr>
                                ) : (
                                    outstandingDocuments.map(
                                        (
                                            document
                                        ) => {
                                            const enteredAmount =
                                                toNumber(
                                                    allocationValues[
                                                        document._id
                                                    ]
                                                );

                                            const exceedsInvoice =
                                                enteredAmount >
                                                toNumber(
                                                    document.balanceAmount
                                                );

                                            return (
                                                <tr
                                                    key={
                                                        document._id
                                                    }
                                                >
                                                    <td>
                                                        <strong>
                                                            {document.documentNumber ||
                                                                "-"}
                                                        </strong>

                                                        <small>
                                                            {document.paymentStatus ||
                                                                ""}
                                                        </small>
                                                    </td>

                                                    <td>
                                                        {formatDate(
                                                            document.documentDate
                                                        )}
                                                    </td>

                                                    <td className="allocate-payment-text-right">
                                                        {formatCurrency(
                                                            document.grandTotal
                                                        )}
                                                    </td>

                                                    <td className="allocate-payment-text-right">
                                                        {formatCurrency(
                                                            document.paidAmount
                                                        )}
                                                    </td>

                                                    <td className="allocate-payment-text-right allocate-payment-balance">
                                                        {formatCurrency(
                                                            document.balanceAmount
                                                        )}
                                                    </td>

                                                    <td className="allocate-payment-allocation-column">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.01"
                                                            value={
                                                                allocationValues[
                                                                    document._id
                                                                ] ??
                                                                ""
                                                            }
                                                            disabled={
                                                                allocating ||
                                                                availableAmount <=
                                                                    0
                                                            }
                                                            className={
                                                                exceedsInvoice
                                                                    ? "allocate-payment-input-error"
                                                                    : ""
                                                            }
                                                            placeholder="0.00"
                                                            onChange={(
                                                                event
                                                            ) =>
                                                                handleAmountChange(
                                                                    document,
                                                                    event
                                                                        .target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>

                    <section className="allocate-payment-summary">
                        <div>
                            <span>
                                Available
                            </span>

                            <strong>
                                {formatCurrency(
                                    availableAmount
                                )}
                            </strong>
                        </div>

                        <div>
                            <span>
                                Allocating
                            </span>

                            <strong
                                className={
                                    isOverAllocated
                                        ? "allocate-payment-over"
                                        : ""
                                }
                            >
                                {formatCurrency(
                                    totalAllocation
                                )}
                            </strong>
                        </div>

                        <div>
                            <span>
                                Remaining
                            </span>

                            <strong>
                                {formatCurrency(
                                    remainingAmount
                                )}
                            </strong>
                        </div>
                    </section>

                    <footer className="allocate-payment-dialog-footer">
                        <button
                            type="button"
                            className="allocate-payment-cancel"
                            disabled={
                                allocating
                            }
                            onClick={
                                handleClose
                            }
                        >
                            Cancel
                        </button>

                        <button
                            type="button"
                            className="allocate-payment-submit"
                            disabled={
                                allocating ||
                                loadingOutstanding ||
                                !customerId ||
                                availableAmount <=
                                    0 ||
                                selectedAllocations.length ===
                                    0 ||
                                isOverAllocated
                            }
                            onClick={
                                handleSubmit
                            }
                        >
                            {allocating ? (
                                <>
                                    <FiLoader className="allocate-payment-spin" />

                                    Allocating...
                                </>
                            ) : (
                                "Allocate Payment"
                            )}
                        </button>
                    </footer>
                </div>
            </div>
        );
    };

export default
    AllocatePaymentDialog;